/**
 * ConversionArchitect.tsx — Top-level page for the Conversion Architect module
 *
 * Single page with sub-components rendered in a phased workflow. Each phase
 * unlocks the next. State flows downward, refresh callbacks flow upward.
 * v2 styling: token module + inline styles (LogicDecomposer precedent).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ProjectSetup from '../components/ConversionArchitect/ProjectSetup';
import StructuralComparison from '../components/ConversionArchitect/StructuralComparison';
import MappingWorkbench from '../components/ConversionArchitect/MappingWorkbench';
import QuestionQueue from '../components/ConversionArchitect/QuestionQueue';
import ValidationSuite from '../components/ConversionArchitect/ValidationSuite';
import OutputGenerator from '../components/ConversionArchitect/OutputGenerator';
import PurposeStrip from '../components/ConversionArchitect/PurposeStrip';
import {
  ConversionProject,
  getProject,
  getSchema,
  listProjects,
} from '../services/conversionArchitectAPI';
import {
  c, font, PHASE_META, PhaseKey, workstreamLabel, btnSecondary,
} from '../components/ConversionArchitect/tokens';

type Phase = PhaseKey;

const PHASE_ORDER: Phase[] = ['setup', 'comparison', 'mapping', 'questions', 'validation', 'output'];

const ConversionArchitect: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ConversionProject[]>([]);
  const [activeProject, setActiveProject] = useState<ConversionProject | null>(null);
  const [activePhase, setActivePhase] = useState<Phase>('setup');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sourceCount, setSourceCount] = useState<number | null>(null);
  const [targetCount, setTargetCount] = useState<number | null>(null);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Header stat readouts: account counts come from the uploaded schemas
  useEffect(() => {
    let cancelled = false;
    setSourceCount(null);
    setTargetCount(null);
    if (!activeProject) return;
    if (activeProject.source_schema_id) {
      getSchema(activeProject.id, 'source')
        .then(d => { if (!cancelled) setSourceCount(d.schema.entity_count); })
        .catch(() => undefined);
    }
    if (activeProject.target_schema_id) {
      getSchema(activeProject.id, 'target')
        .then(d => { if (!cancelled) setTargetCount(d.schema.entity_count); })
        .catch(() => undefined);
    }
    return () => { cancelled = true; };
  }, [activeProject?.id, activeProject?.source_schema_id, activeProject?.target_schema_id]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const data = await listProjects();
      setProjects(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const refreshProject = useCallback(async () => {
    if (!activeProject) return;
    try {
      const updated = await getProject(activeProject.id);
      setActiveProject(updated);
    } catch (err: any) {
      console.error('Failed to refresh project:', err);
    }
  }, [activeProject]);

  const handleProjectSelect = async (project: ConversionProject) => {
    setActiveProject(project);
    // Auto-advance to the right phase based on project status
    if (project.status === 'complete') {
      setActivePhase('output');
    } else if (project.status === 'validating') {
      setActivePhase('validation');
    } else if (project.status === 'mapping' && project.source_schema_id && project.target_schema_id) {
      setActivePhase('mapping');
    } else if (project.source_schema_id && project.target_schema_id) {
      setActivePhase('comparison');
    } else {
      setActivePhase('setup');
    }
  };

  const handleProjectCreated = (project: ConversionProject) => {
    setProjects(prev => [project, ...prev]);
    setActiveProject(project);
    setActivePhase('setup');
  };

  const handlePhaseComplete = (phase: Phase) => {
    const idx = PHASE_ORDER.indexOf(phase);
    if (idx < PHASE_ORDER.length - 1) {
      setActivePhase(PHASE_ORDER[idx + 1]);
    }
    refreshProject();
  };

  const canAccessPhase = (phase: Phase): boolean => {
    if (!activeProject) return phase === 'setup';
    const idx = PHASE_ORDER.indexOf(phase);
    if (idx === 0) return true;

    switch (phase) {
      case 'comparison':
        return !!(activeProject.source_schema_id && activeProject.target_schema_id);
      case 'mapping':
        return activeProject.status !== 'draft' || !!(activeProject.summary_stats?.total_rules);
      case 'questions':
        return !!(activeProject.summary_stats?.questions_total);
      case 'validation':
        return !!(activeProject.summary_stats?.total_rules);
      case 'output':
        return !!(activeProject.summary_stats?.total_rules);
      default:
        return true;
    }
  };

  const stats = activeProject?.summary_stats;
  const waitingCount = stats ? stats.questions_open : null;
  const stepNumber = PHASE_ORDER.indexOf(activePhase) + 1;
  const meta = PHASE_META[activePhase];

  if (loading) {
    return (
      <div style={{ background: c.canvas, minHeight: '100vh', padding: 40, fontFamily: font.ui }}>
        <p style={{ fontSize: 13, color: c.textSecondary }}>Loading Conversion Architect…</p>
      </div>
    );
  }

  return (
    <div style={{ background: c.canvas, minHeight: '100vh', padding: '20px 24px 60px', fontFamily: font.ui }}>
      <button
        onClick={() => navigate('/app/intelligence')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          background: 'transparent', border: 'none', padding: 0, marginBottom: 12,
          fontSize: 12.5, fontWeight: 600, color: c.textSecondary, fontFamily: font.ui,
        }}
      >
        ← Back to Jarvis Intelligence
      </button>
      {/* Header strip */}
      <div
        style={{
          background: c.card,
          border: `1px solid ${c.border}`,
          borderRadius: 12,
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 20,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: c.textPrimary }}>Conversion Architect</div>
          {activeProject && (
            <div style={{ fontFamily: font.mono, fontSize: 11, color: c.textSecondary, marginTop: 3 }}>
              {activeProject.name} · {workstreamLabel(activeProject.workstream)} · EBS → Fusion
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 26 }}>
          {activeProject && sourceCount !== null && targetCount !== null && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: font.mono, fontSize: 22, fontWeight: 800, color: c.textPrimary }}>
                {sourceCount} → {targetCount}
              </div>
              <div style={{ fontSize: 11, color: c.textSecondary }}>accounts</div>
            </div>
          )}
          {activeProject && waitingCount !== null && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: font.mono, fontSize: 22, fontWeight: 800, color: c.amber.text2 }}>
                {waitingCount}
              </div>
              <div style={{ fontSize: 11, color: c.textSecondary }}>waiting on you</div>
            </div>
          )}
          {activeProject && (
            <button
              type="button"
              style={btnSecondary()}
              onClick={() => {
                setActiveProject(null);
                setActivePhase('setup');
              }}
            >
              Switch project
            </button>
          )}
        </div>
      </div>

      {/* Phase tabs */}
      {activeProject && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            borderBottom: `1px solid ${c.border}`,
            marginBottom: 16,
          }}
        >
          {PHASE_ORDER.map(phase => {
            const accessible = canAccessPhase(phase);
            const isActive = phase === activePhase;
            return (
              <button
                key={phase}
                type="button"
                onClick={() => accessible && setActivePhase(phase)}
                disabled={!accessible}
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderBottom: isActive ? `3px solid ${c.textPrimary}` : '3px solid transparent',
                  padding: '10px 14px',
                  fontFamily: font.ui,
                  fontSize: 13.5,
                  fontWeight: isActive ? 800 : 600,
                  color: isActive ? c.textPrimary : c.textSecondary,
                  opacity: accessible ? 1 : 0.4,
                  cursor: accessible ? 'pointer' : 'default',
                }}
              >
                {PHASE_META[phase].label}
              </button>
            );
          })}
          <div style={{ marginLeft: 'auto', fontSize: 11.5, color: c.textTertiary, paddingRight: 4 }}>
            Step {stepNumber} of 6
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div
          style={{
            background: c.red.fill,
            border: `1px solid ${c.red.border}`,
            color: c.red.text,
            borderRadius: 10,
            padding: '10px 16px',
            marginBottom: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 13,
          }}
        >
          <span>{error}</span>
          <button
            type="button"
            aria-label="Dismiss error"
            onClick={() => setError(null)}
            style={{ background: 'transparent', border: 'none', color: c.red.text, fontWeight: 800, cursor: 'pointer', fontSize: 14 }}
          >
            ×
          </button>
        </div>
      )}

      {/* Purpose strip for the current phase */}
      <PurposeStrip forText={meta.forText} doText={meta.doText} oracleText={meta.oracleText} />

      {/* Phase content */}
      <div>
        {activePhase === 'setup' && (
          <ProjectSetup
            projects={projects}
            activeProject={activeProject}
            onProjectSelect={handleProjectSelect}
            onProjectCreated={handleProjectCreated}
            onSchemaUploaded={() => {
              refreshProject();
            }}
            onPhaseComplete={() => handlePhaseComplete('setup')}
          />
        )}

        {activePhase === 'comparison' && activeProject && (
          <StructuralComparison
            project={activeProject}
            onPhaseComplete={() => handlePhaseComplete('comparison')}
          />
        )}

        {activePhase === 'mapping' && activeProject && (
          <MappingWorkbench
            project={activeProject}
            onPhaseComplete={() => handlePhaseComplete('mapping')}
            onRefresh={refreshProject}
          />
        )}

        {activePhase === 'questions' && activeProject && (
          <QuestionQueue
            project={activeProject}
            onPhaseComplete={() => handlePhaseComplete('questions')}
            onRefresh={refreshProject}
          />
        )}

        {activePhase === 'validation' && activeProject && (
          <ValidationSuite
            project={activeProject}
            onPhaseComplete={() => handlePhaseComplete('validation')}
          />
        )}

        {activePhase === 'output' && activeProject && (
          <OutputGenerator project={activeProject} />
        )}
      </div>
    </div>
  );
};

export default ConversionArchitect;
